import { Flex, Show, Spinner } from '@chakra-ui/react';
import type React from 'react';
import { useState } from 'react';
import { FaceliftScrollContainer } from '@/components/Atoms/FaceliftScrollContainer';
import FaceliftSuccessProgressModal from '@/components/Molecules/FaceliftSuccessProgressModal';
import { useCreateUser, useUpdateUser } from '@/hooks/service-hooks/users';
import type { User } from '@/types/users';
import { trackCoreClick } from '@/utils/analytics';
import UsersRolesForm from '../UsersRolesForm';
import type { UsersRolesFormData } from '../UsersRolesForm/types';

interface CreateEditUsersRolesProps {
	mode: 'create' | 'edit';
	initialData?: Partial<UsersRolesFormData>;
	onSuccess?: (message: string) => void;
	isLoading?: boolean;
	onIsDirtyChange?: (isDirty: boolean) => void;
}
interface ApiError {
  response?: {
    data?: {
      error?: {
        message?: {
          message?: string;
        };
      };
    };
  };
}

const CreateEditUsersRoles: React.FC<CreateEditUsersRolesProps> = ({
	mode,
	initialData,
	onSuccess,
	isLoading,
	onIsDirtyChange,
}) => {
	const [errorMessage, setErrorMessage] = useState('');
	const {
		mutateAsync: createUser,
		isLoading: createLoading,
		isSuccess: createSuccess,
		isError: createError,
	} = useCreateUser(
		() => {
			onSuccess?.('User created successfully');
		},
		(error) => {
      const apiError = error as ApiError;
			setErrorMessage(
				apiError?.response?.data?.error?.message?.message ||
					'Failed to create user. Please try again.',
			);
		},
	);

	const {
		mutateAsync: updateUser,
		isLoading: updateLoading,
		isSuccess: updateSuccess,
		isError: updateError,
	} = useUpdateUser(
		() => {
			onSuccess?.('User updated successfully');
		},
		(error) => {
			const apiError = error as ApiError;
			setErrorMessage(
				apiError?.response?.data?.error?.message?.message ||
					'Failed to update user. Please try again.',
			);
		},
	);

	const handleSubmitUsersRoles = async (formData: UsersRolesFormData) => {
		const payload: User = {
			firstName: formData.firstName.trim(),
			lastName: formData.lastName.trim(),
			email: formData.email.trim(),
			role: formData.role.trim(),
			clinics: formData.clinics?.map((clinic) => clinic.value as string),
			locations: formData.port === 'SalesRep' ? formData.consignments?.map((consignment) => consignment.value.toString()) : [],
			status: formData.status === 'enabled',
			autoSyncClinics: formData.autoSyncClinics,
			salesReps: formData.port === 'SalesRep' ? formData.salesReps?.map((salesRep) => salesRep.value.toString()) : [],
			permissions: formData.permissions,
			environmentsAvailable: formData.environments,
			notificationTypes: formData.notificationTypes,
			notificationsEnabled: formData.notificationsEnabled,
			portsAvailable: formData.port.trim(),
			isExternal: formData.userType.trim() === 'external',
		};
		trackCoreClick('submit_user_clicked', {
			source_module: 'manage_users_form',
			form_mode: mode,
			target_user_id:
				mode === 'edit' && typeof initialData?.id === 'string'
					? initialData.id
					: '',
			managed_role: payload.role,
			managed_port: payload.portsAvailable,
			clinic_count: payload.clinics?.length || 0,
		});

		if (mode === 'create') {
			await createUser(payload);
		} else if (mode === 'edit') {
			const userId = initialData?.id;
			if (userId) {
				await updateUser({ id: userId, data: payload });
			}
		}
	};

	return (
		<>
			<FaceliftSuccessProgressModal
				open={
					createLoading ||
					updateLoading ||
					createSuccess ||
					updateSuccess ||
					createError ||
					updateError
				}
				isLoading={createLoading || updateLoading}
				isSucceed={createSuccess || updateSuccess}
				hasError={createError || updateError}
				onOpenChange={() => {}}
				title={mode === 'create' ? 'User Created!' : 'User Updated!'}
				errorMessage={
					createError
						? errorMessage || 'Error creating user'
						: updateError
							? errorMessage || 'Error updating user'
							: ''
				}
			/>
			<FaceliftScrollContainer
				maxH="calc(90vh - 200px)"
				overflowY="auto"
				minWidth="fit-content"
				overflowX="visible"
				pb={5}
			>
				<Show
					when={isLoading}
					fallback={
						<UsersRolesForm
							mode={mode}
							initialData={initialData}
							onSubmit={handleSubmitUsersRoles}
							isLoading={createLoading || updateLoading}
							onIsDirtyChange={onIsDirtyChange}
						/>
					}
				>
					<Flex justify="center" align="center" minHeight="300px" width="100%">
						<Spinner size="xl" colorPalette="brand" />
					</Flex>
				</Show>
			</FaceliftScrollContainer>
		</>
	);
};

export default CreateEditUsersRoles;
